import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ActivityIndicator, Button, Tabs, TextLink } from '@kontentino/ui';
import clsx from 'clsx';
import Link from 'components/shared/Link';
import routes from 'constants/routes';
import useInfiniteScroll from 'utils/hooks/useInfiniteScroll';
import InAppNotificationsRequest, {
  InAppNotificationsRequestParams,
} from 'components/in-app-notifications/InAppNotificationsRequest';
import NotificationItem from 'components/in-app-notifications/NotificationItem';
import { useMarkAllNotificationsAsRead } from 'components/in-app-notifications/InAppNotifications.hooks';
import {
  NOTIFICATIONS_FILTER,
  NotificationsFilter,
} from 'components/in-app-notifications/InAppNotifications.constants';
import {
  groupNotificationsByAge,
  NOTIFICATION_GROUP_LABEL,
} from 'components/in-app-notifications/notificationGroups';

function NotificationsTooltipPanel({
  data,
  isError,
  isLoading,
  isLoadingMore,
  canLoadMore,
  loadMore,
}: InAppNotificationsRequestParams) {
  const { t } = useTranslation();
  const [filter, setFilter] = useState<NotificationsFilter>(
    NOTIFICATIONS_FILTER.Unread,
  );
  const { mutate: markAllAsRead } = useMarkAllNotificationsAsRead();

  const hasUnread = data.some((notification) => !notification.is_read);
  // Auto-loading is limited to the All tab: the unread filter runs over already
  // loaded pages, so scrolling an all-read history would chain-request every
  // page just to keep showing an empty list.
  const isAutoLoadEnabled = filter === NOTIFICATIONS_FILTER.All;

  const [sentryRef, { rootRef }] = useInfiniteScroll({
    loading: isLoading || isLoadingMore,
    hasNextPage: canLoadMore,
    onLoadMore: loadMore,
    disabled: isError || !isAutoLoadEnabled,
    rootMargin: '0px 0px 200px 0px',
  });

  const groups = useMemo(() => {
    const visibleNotifications =
      filter === NOTIFICATIONS_FILTER.Unread
        ? data.filter((notification) => !notification.is_read)
        : data;

    return groupNotificationsByAge(visibleNotifications);
  }, [data, filter]);

  const isEmpty = groups.length === 0;

  return (
    <div className="tw-flex tw-h-full tw-flex-col tw-overflow-hidden tw-rounded-xl tw-bg-white tw-shadow-md tw-outline-none">
      <div className="tw-flex tw-min-h-[44px] tw-items-center tw-justify-between tw-gap-2 tw-px-4 tw-pt-3">
        <div className="tw-text-md tw-font-semibold tw-text-grayscale-180">
          {t('notifications.title')}
        </div>
        {hasUnread && (
          <Button
            variant="tertiary"
            size="small"
            onClick={() => markAllAsRead()}
            data-cy="navbar_notifications_mark-all-as-read"
          >
            {t('notifications.markAllAsRead')}
          </Button>
        )}
      </div>
      <div className="tw-flex tw-items-center tw-justify-between tw-gap-2 tw-border-b tw-border-grayscale-10 tw-px-4">
        <Tabs.Root
          value={filter}
          onValueChange={(value) => setFilter(value as NotificationsFilter)}
        >
          <Tabs.List>
            <Tabs.Tab
              value={NOTIFICATIONS_FILTER.Unread}
              data-cy="navbar_notifications_filter-new"
            >
              {t('notifications.new')}
            </Tabs.Tab>
            <Tabs.Tab
              value={NOTIFICATIONS_FILTER.All}
              data-cy="navbar_notifications_filter-all"
            >
              {t('all')}
            </Tabs.Tab>
          </Tabs.List>
        </Tabs.Root>
        <TextLink>
          <Link
            to={routes.IN_APP_NOTIFICATIONS}
            isRouterLink
            data-cy="navbar_notifications_show-all"
          >
            {t('showAll')}
          </Link>
        </TextLink>
      </div>
      <div
        ref={rootRef}
        className="tw-flex-1 tw-overflow-y-auto tw-p-3"
        data-cy="navbar_notifications_list"
      >
        {isLoading && (
          <div className="tw-flex tw-h-full tw-items-center tw-justify-center tw-p-3">
            <ActivityIndicator className="tw-text-primary-100" />
          </div>
        )}
        {!isLoading && isError && data.length === 0 && (
          <div className="tw-flex tw-h-full tw-items-center tw-justify-center tw-p-6 tw-text-center tw-text-sm tw-text-grayscale-90">
            {t('somethingWentWrong')}
          </div>
        )}
        {!isLoading && !isError && isEmpty && (
          <div
            className={clsx(
              'tw-flex tw-items-center tw-justify-center tw-p-6 tw-text-center tw-text-sm tw-text-grayscale-90',
              { 'tw-h-full': !canLoadMore },
            )}
            data-cy="navbar_notifications_empty"
          >
            {filter === NOTIFICATIONS_FILTER.Unread
              ? t('noUnreadNotifications')
              : t('notifications.empty')}
          </div>
        )}
        {groups.map((group) => (
          <div key={group.key}>
            <div className="tw-px-3 tw-pb-1 tw-pt-3 tw-text-xs tw-font-semibold tw-uppercase tw-tracking-wide tw-text-grayscale-90">
              {t(NOTIFICATION_GROUP_LABEL[group.key])}
            </div>
            {group.notifications.map((notification) => (
              <NotificationItem
                key={notification.id}
                notification={notification}
              />
            ))}
          </div>
        ))}
        {canLoadMore && !isError && isAutoLoadEnabled && (
          <div
            ref={sentryRef}
            className="tw-flex tw-items-center tw-justify-center tw-p-3"
          >
            <ActivityIndicator className="tw-text-primary-100" />
          </div>
        )}
        {canLoadMore && !isError && !isAutoLoadEnabled && (
          <div className="tw-p-3">
            <Button
              variant="tertiary"
              className="tw-w-full"
              onClick={loadMore}
              isLoading={isLoadingMore}
              disabled={isLoadingMore}
              data-cy="navbar_notifications_load-more"
            >
              {t('notifications.loadMore')}
            </Button>
          </div>
        )}
      </div>
    </div>
  );
}

function NotificationsTooltipContent() {
  return (
    <InAppNotificationsRequest>
      {(params) => <NotificationsTooltipPanel {...params} />}
    </InAppNotificationsRequest>
  );
}

export default NotificationsTooltipContent;
